import React from 'react';
import zxcvbn from 'zxcvbn'; // https://github.com/dropbox/zxcvbn
// @ts-expect-error TS(2305): Module '"prop-types"' has no exported member 'Prop... Remove this comment to see the full error message
import { PropTypes } from 'prop-types';

import { mui } from '~/tools/mui';
import { icons } from '~/tools/icons';
import { jss } from '~/tools/jss';
import { useTranslation } from '~/behaviors';

const PASSWORD_MIN_CHARS = 8;
const PASSWORD_MIN_SCORE = 2;
const PW_RED = '#d1462f';
const PW_ORANGE = '#d09d00';
const PW_BLUE = '#57b8ff';
const PW_GREEN = '#2fbf71';

const isTooShort = (password, minLength) => password.length < minLength;

const useStyle = jss.makeStyles((theme) => ({
  strengthBar: {
    boxSizing: 'border-box',
    height: '4px',
    width: 0,
    backgroundColor: 'transparent',
    position: 'relative',
    top: '1px',
    right: '0',
    transition: 'width 300ms ease-out',
  },
  'strengthBar-0': {
    backgroundColor: PW_RED,
    width: '20%',
  },
  'strengthBar-1': {
    backgroundColor: PW_RED,
    width: '40%',
  },
  'strengthBar-2': {
    backgroundColor: PW_ORANGE,
    width: '60%',
  },
  'strengthBar-3': {
    backgroundColor: PW_BLUE,
    width: '80%',
  },
  'strengthBar-4': {
    backgroundColor: PW_GREEN,
    width: '100%',
  },
  strengthDescContainer: {
    paddingTop: 5,
  },
  strengthDesc: {
    fontSize: '1em',
    fontFamily: theme.typography.fontFamily,
  },
  'strengthDesc-0': {
    color: PW_RED,
  },
  'strengthDesc-1': {
    color: PW_RED,
  },
  'strengthDesc-2': {
    color: PW_ORANGE,
  },
  'strengthDesc-3': {
    color: PW_BLUE,
  },
  'strengthDesc-4': {
    color: PW_GREEN,
  },
  tooltipContainer: {
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    bottom: 0,
    maxWidth: 400,
    marginLeft: 8,
    fontSize: '1.2em',
    color: '#ccc',
    '&:hover': {
      color: '#000',
    },
  },
  tooltipText: {
    fontSize: '1.2em',
    lineHeight: '1.4em',
    padding: 4,
  },
}));

const PasswordStrengthChecker = ({
  password = '',
  userInputs = [],
  minLength = PASSWORD_MIN_CHARS,
  minScore: _ = PASSWORD_MIN_SCORE,
}) => {
  // @ts-expect-error TS(2339): Property 'propTypes' does not exist on type '({ pa... Remove this comment to see the full error message
  PasswordStrengthChecker.propTypes = {
    password: PropTypes.string.isRequired,
    userInputs: PropTypes.array.isRequired,
    minLength: PropTypes.number,
    minScore: PropTypes.number,
  };

  const { t } = useTranslation();
  const styles = useStyle();

  const hasValue = password.length > 0;
  const result = zxcvbn(password, userInputs);
  const score = result.score || 0;

  // REFACTOR: Apply translation process to these values.
  const scoreWords = ['weak', 'weak', 'okay', 'good', 'strong'];
  const tooShortWord = 'too short';

  const strengthDescriptionStyle = hasValue ? styles[`strengthDesc-${score}`] : null;
  const strengthBarStyle = hasValue ? styles[`strengthBar-${score}`] : null;

  const strengthDescription = isTooShort(password, minLength) ? tooShortWord : scoreWords[score];

  if (!hasValue) return null;

  return (
    <>
      <div className={jss.combineStyles(styles.strengthBar, strengthBarStyle)} data-testid="password-strength-bar" />

      <div className={styles.strengthDescContainer} data-testid="password-strength-description">
        <span className={jss.combineStyles(styles.strengthDesc, strengthDescriptionStyle)}>{strengthDescription}</span>
        <span className={styles.tooltipContainer}>
          <mui.Tooltip
            title={<mui.Typography className={styles.tooltipText}>{t('password.strength.tooltip')}</mui.Typography>}
            className={styles.tooltip}
            disableInteractive
          >
            <icons.Info />
          </mui.Tooltip>
        </span>
      </div>
    </>
  );
};

export default PasswordStrengthChecker;
